/**
 * Planar position analysis (a.k.a. the "assembly" or "loop-closure" problem):
 * given the design parameters and one value of the driver angle theta, find the
 * coordinates of every free joint such that all rigid bars keep their length.
 *
 * We solve `g(q) = 0`, where each bar (a, b, L) contributes
 *   g_k = |p_a - p_b|^2 - L^2 = 0
 * and `q` stacks the (x, y) of every free joint. For a well-constrained 1-DOF
 * linkage the number of bars equals `2 * F`, so the system is square and solved
 * by Newton-Raphson with an analytic Jacobian.
 *
 * Differentiability
 * -----------------
 * Unrolling every Newton iteration through the autodiff tape is wasteful and
 * numerically noisy. Instead we converge in plain floats, then take exactly one
 * Newton step *under autodiff* starting from the converged point q*:
 *
 *   q_diff = q*  -  J(q*, p)^{-1} g(q*, p)
 *
 * with q* held constant and the parameters `p` live. Because g(q*, p) is ~0 in
 * value but not in derivative, this reproduces the implicit-function-theorem
 * result  dq/dp = -J^{-1} (dg/dp)  in a single linear solve. See docs/THEORY.md.
 */

import { Value, V } from "../autodiff/value.js";
import { Vec2 } from "../autodiff/vec2.js";
import { MechanismSpec, groundCount, freeCount } from "./types.js";
import {
  SingularSystemError,
  solveDenseNumeric,
  solveDenseValue,
} from "./linsolve.js";

export interface PositionSolution {
  /** Flat free-joint coordinates, length 2F. */
  q: number[];
  /** Coordinates of every joint, length = jointCount. */
  positions: Array<[number, number]>;
  /** Coupler (traced) point. */
  coupler: [number, number];
  converged: boolean;
  iterations: number;
  /** L2 norm of the final constraint residual. */
  residualNorm: number;
}

// ---------------------------------------------------------------------------
// Numeric evaluation
// ---------------------------------------------------------------------------

/** Free-joint slot for a flat joint index, or -1 if the joint is not free. */
function freeSlot(m: MechanismSpec, jointIdx: number): number {
  const start = groundCount(m) + 1;
  return jointIdx >= start ? jointIdx - start : -1;
}

/** Position of the driven joint (crank tip) for input angle `theta`. */
function drivenPosNumeric(m: MechanismSpec, theta: number): [number, number] {
  const [bx, by] = m.grounds[m.driver.base];
  const a = m.driver.dir * theta + m.driver.phase;
  return [bx + m.driver.length * Math.cos(a), by + m.driver.length * Math.sin(a)];
}

/** All joint positions given free coordinates `q`. */
function jointPositionsNumeric(
  m: MechanismSpec,
  theta: number,
  q: number[],
): Array<[number, number]> {
  const G = groundCount(m);
  const pos: Array<[number, number]> = [];
  for (let i = 0; i < G; i++) pos.push([m.grounds[i][0], m.grounds[i][1]]);
  pos.push(drivenPosNumeric(m, theta));
  for (let i = 0; i < freeCount(m); i++) pos.push([q[2 * i], q[2 * i + 1]]);
  return pos;
}

function residualNumeric(m: MechanismSpec, pos: Array<[number, number]>): number[] {
  return m.bars.map(([ia, ib, L]) => {
    const dx = pos[ia][0] - pos[ib][0];
    const dy = pos[ia][1] - pos[ib][1];
    return dx * dx + dy * dy - L * L;
  });
}

function jacobianNumeric(
  m: MechanismSpec,
  pos: Array<[number, number]>,
): number[][] {
  const F = freeCount(m);
  const J: number[][] = m.bars.map(() => new Array<number>(2 * F).fill(0));
  m.bars.forEach(([ia, ib], k) => {
    const dx = pos[ia][0] - pos[ib][0];
    const dy = pos[ia][1] - pos[ib][1];
    const sa = freeSlot(m, ia);
    const sb = freeSlot(m, ib);
    if (sa >= 0) {
      J[k][2 * sa] += 2 * dx;
      J[k][2 * sa + 1] += 2 * dy;
    }
    if (sb >= 0) {
      J[k][2 * sb] += -2 * dx;
      J[k][2 * sb + 1] += -2 * dy;
    }
  });
  return J;
}

function couplerNumeric(
  m: MechanismSpec,
  pos: Array<[number, number]>,
): [number, number] {
  const [ia, ib] = m.bars[m.coupler.bar];
  const ax = pos[ia][0];
  const ay = pos[ia][1];
  const dx = pos[ib][0] - ax;
  const dy = pos[ib][1] - ay;
  const len = Math.hypot(dx, dy) || 1e-30;
  const ux = dx / len;
  const uy = dy / len;
  // local +x is u-hat, local +y is u-hat rotated +90 deg = (-uy, ux)
  return [
    ax + m.coupler.u * ux + m.coupler.v * -uy,
    ay + m.coupler.u * uy + m.coupler.v * ux,
  ];
}

// ---------------------------------------------------------------------------
// Newton solve (numeric)
// ---------------------------------------------------------------------------

export interface SolveOptions {
  maxIterations?: number;
  tolerance?: number;
  /** Levenberg-style damping added to the Jacobian diagonal if it goes singular. */
  damping?: number;
}

/**
 * Solve the position problem for one driver angle, seeded from `seed`
 * (length 2F). Returns the solution plus convergence diagnostics; never throws
 * on non-convergence — check `.converged`.
 */
export function solvePosition(
  m: MechanismSpec,
  theta: number,
  seed: number[],
  opts: SolveOptions = {},
): PositionSolution {
  const maxIterations = opts.maxIterations ?? 60;
  const tolerance = opts.tolerance ?? 1e-11;
  const F = freeCount(m);

  if (m.bars.length !== 2 * F) {
    throw new Error(
      `mechanism is not square: ${m.bars.length} bars but ${2 * F} free DOF`,
    );
  }

  let q = seed.slice();
  let pos = jointPositionsNumeric(m, theta, q);
  let res = residualNumeric(m, pos);
  let iterations = 0;
  let converged = false;

  for (let it = 0; it < maxIterations; it++) {
    iterations = it + 1;
    const norm = Math.hypot(...res);
    if (norm < tolerance) {
      converged = true;
      break;
    }
    const J = jacobianNumeric(m, pos);
    let delta: number[];
    try {
      delta = solveDenseNumeric(J, res);
    } catch (e) {
      if (e instanceof SingularSystemError) {
        // one damped retry
        const d = opts.damping ?? 1e-6;
        for (let i = 0; i < J.length; i++) J[i][i] += d;
        try {
          delta = solveDenseNumeric(J, res);
        } catch {
          break;
        }
      } else throw e;
    }
    // Newton step with a simple backtracking line search for robustness.
    let step = 1;
    let accepted = false;
    for (let ls = 0; ls < 8; ls++) {
      const qTry = q.map((v, i) => v - step * delta[i]);
      const posTry = jointPositionsNumeric(m, theta, qTry);
      const resTry = residualNumeric(m, posTry);
      if (Math.hypot(...resTry) < norm) {
        q = qTry;
        pos = posTry;
        res = resTry;
        accepted = true;
        break;
      }
      step *= 0.5;
    }
    if (!accepted) {
      // take the undamped full step and let the next iteration judge it
      q = q.map((v, i) => v - delta[i]);
      pos = jointPositionsNumeric(m, theta, q);
      res = residualNumeric(m, pos);
    }
  }

  const residualNorm = Math.hypot(...res);
  return {
    q,
    positions: pos,
    coupler: couplerNumeric(m, pos),
    converged: converged || residualNorm < 1e-7,
    iterations,
    residualNorm,
  };
}

// ---------------------------------------------------------------------------
// Differentiable single Newton step
// ---------------------------------------------------------------------------

/** Parameters lifted into Value space, mirroring {@link unpackParams}. */
export interface MechanismValues {
  grounds: Vec2[];
  crankLength: Value;
  crankPhase: Value;
  barLengths: Value[];
  couplerU: Value;
  couplerV: Value;
}

/** Lift a packed parameter vector of Values into the structured form. */
export function liftParams(base: MechanismSpec, p: Value[]): MechanismValues {
  const G = base.grounds.length;
  let k = 0;
  const grounds: Vec2[] = [];
  for (let i = 0; i < G; i++) grounds.push(new Vec2(p[k++], p[k++]));
  const crankLength = p[k++];
  const crankPhase = p[k++];
  const barLengths = base.bars.map((_, i) => p[k + i]);
  k += base.bars.length;
  const couplerU = p[k++];
  const couplerV = p[k++];
  return { grounds, crankLength, crankPhase, barLengths, couplerU, couplerV };
}

function drivenPosValue(
  base: MechanismSpec,
  mv: MechanismValues,
  theta: number,
): Vec2 {
  const angle = mv.crankPhase.add(base.driver.dir * theta);
  return mv.grounds[base.driver.base].add(Vec2.fromAngle(angle, mv.crankLength));
}

function jointPositionsValue(
  base: MechanismSpec,
  mv: MechanismValues,
  theta: number,
  qStar: Value[],
): Vec2[] {
  const G = base.grounds.length;
  const pos: Vec2[] = [];
  for (let i = 0; i < G; i++) pos.push(mv.grounds[i]);
  pos.push(drivenPosValue(base, mv, theta));
  for (let i = 0; i < freeCount(base); i++) {
    pos.push(new Vec2(qStar[2 * i], qStar[2 * i + 1]));
  }
  return pos;
}

function couplerValue(base: MechanismSpec, mv: MechanismValues, pos: Vec2[]): Vec2 {
  const [ia, ib] = base.bars[base.coupler.bar];
  const a = pos[ia];
  const d = pos[ib].sub(a);
  const u = d.unit();
  const uPerp = u.perp();
  return a.add(u.scale(mv.couplerU)).add(uPerp.scale(mv.couplerV));
}

export interface DifferentiablePose {
  /** All joint positions as Values (function of the parameters). */
  positions: Vec2[];
  /** Coupler point as a Value (function of the parameters). */
  coupler: Vec2;
}

/**
 * Given a converged numeric solution `q*` for driver angle `theta` and the
 * parameters as Values `p`, build the differentiable joint/coupler positions
 * via one Newton step from q*. The returned Values depend on `p` with correct
 * first derivatives.
 */
export function differentiablePose(
  base: MechanismSpec,
  p: Value[],
  theta: number,
  qStarNumbers: number[],
): DifferentiablePose {
  const mv = liftParams(base, p);
  const F = freeCount(base);

  // q* as constant leaves (no upstream): the Newton step differentiates them
  // implicitly through the residual, not directly.
  const qStar = qStarNumbers.map((v) => V(v));
  const pos = jointPositionsValue(base, mv, theta, qStar);

  // Residual g(q*, p) as Values.
  const g: Value[] = base.bars.map(([ia, ib], k) => {
    const d = pos[ia].sub(pos[ib]);
    return d.normSq().sub(mv.barLengths[k].square());
  });

  // Analytic Jacobian dg/dq (2F x 2F) as Values, evaluated at q*.
  const J: Value[][] = base.bars.map(() =>
    Array.from({ length: 2 * F }, () => V(0)),
  );
  base.bars.forEach(([ia, ib], k) => {
    const d = pos[ia].sub(pos[ib]); // Vec2 of Values
    const sa = freeSlot(base, ia);
    const sb = freeSlot(base, ib);
    if (sa >= 0) {
      J[k][2 * sa] = d.x.mul(2);
      J[k][2 * sa + 1] = d.y.mul(2);
    }
    if (sb >= 0) {
      J[k][2 * sb] = d.x.mul(-2);
      J[k][2 * sb + 1] = d.y.mul(-2);
    }
  });

  const delta = solveDenseValue(J, g); // J^{-1} g
  const qNew = qStar.map((qs, i) => qs.sub(delta[i]));

  // Rebuild positions with the differentiable q.
  const G = base.grounds.length;
  const posDiff: Vec2[] = [];
  for (let i = 0; i < G; i++) posDiff.push(mv.grounds[i]);
  posDiff.push(drivenPosValue(base, mv, theta));
  for (let i = 0; i < F; i++) posDiff.push(new Vec2(qNew[2 * i], qNew[2 * i + 1]));

  return { positions: posDiff, coupler: couplerValue(base, mv, posDiff) };
}
